import React, { useState, useEffect } from 'react';
import SpecialityDropdown from '../../Dropdown/SpecialityDropdown';
import AssetsDropdown from '../../Dropdown/Assetsdropdown';
import LocationDropdown from '../../Dropdown/LocationName';
import AreaDropdown from '../../Dropdown/AreaDropdown';

const CaseReferenceAddModal = ({
  show,
  onClose,
  onSave,
  formData,
  isEditMode,
}) => {
  const [caseName, setCaseName] = useState('');
  const [caseType, setCaseType] = useState('');
  const [specialities, setSpecialities] = useState(['']);
  const [assets, setAssets] = useState([{ id: '', name: '', quantity: '' }]);
  const [locationId, setLocationId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [validatedFields, setValidatedFields] = useState({
    caseName: false,
    caseType: false,
    specialities: false,
    assets: false,
    locationId: false,
    areaId: false
  });
  const [touchedFields, setTouchedFields] = useState({
    caseName: false,
    caseType: false,
    specialities: false,
    assets: false,
    locationId: false,
    areaId: false
  });

  useEffect(() => {
    if (isEditMode && formData) {
      setCaseName(formData.name || '');
      setCaseType(formData.type || '');
      setSpecialities(formData.specialities?.length ? [...formData.specialities] : ['']);
      setAssets(
        formData.assets?.length
          ? formData.assets.map(a => ({
            id: a.id || '',
            name: a.name?.toString() || '',
            quantity: a.quantity?.toString() || ''
          }))
          : [{ id: '', name: '', quantity: '' }]
      );
      setLocationId(formData.location_id?.toString() || '');
      setAreaId(formData.area_id?.toString() || '');
    } else {
      setCaseName('');
      setCaseType('');
      setSpecialities(['']);
      setAssets([{ id: '', name: '', quantity: '' }]);
      setLocationId('');
      setAreaId('');
      setValidatedFields({
        caseName: false,
        caseType: false,
        specialities: false,
        assets: false,
        locationId: false,
        areaId: false
      });
      setTouchedFields({
        caseName: false,
        caseType: false,
        specialities: false,
        assets: false,
        locationId: false,
        areaId: false
      });
    }
  }, [show, isEditMode, formData]);

  const handleAddSpeciality = () => {
    setSpecialities([...specialities, '']);
    setValidatedFields(prev => ({ ...prev, specialities: false }));
  };

  const handleRemoveSpeciality = (index) => {
    const updated = [...specialities];
    updated.splice(index, 1);
    setSpecialities(updated);
    const isValid = updated.length > 0 && updated.some(s => s);
    setValidatedFields(prev => ({ ...prev, specialities: isValid }));
  };

  const handleSpecialityChange = (value, index) => {
    const updated = [...specialities];
    updated[index] = value;
    setSpecialities(updated);
    if (touchedFields.specialities) {
      const isValid = updated.length > 0 && updated.some(s => s);
      setValidatedFields(prev => ({ ...prev, specialities: isValid }));
    }
  };

  const handleAddAsset = () => {
    setAssets([...assets, { id: '', name: '', quantity: '' }]);
    setValidatedFields(prev => ({ ...prev, assets: false }));
  };

  const handleRemoveAsset = (index) => {
    const updated = [...assets];
    updated.splice(index, 1);
    setAssets(updated);
    const isValid = updated.length > 0 && updated.some(a => a.name && a.quantity);
    setValidatedFields(prev => ({ ...prev, assets: isValid }));
  };

  const handleAssetChange = (index, field, value) => {
    const updated = [...assets];
    updated[index][field] = value;
    setAssets(updated);
    if (touchedFields.assets) {
      const isValid = updated.length > 0 && updated.some(a => a.name && a.quantity);
      setValidatedFields(prev => ({ ...prev, assets: isValid }));
    }
  };

  const handleBlur = (fieldName) => {
    if (!touchedFields[fieldName]) {
      setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    }
    if (!validatedFields[fieldName]) {
      validateField(fieldName);
    }
  };

  const validateField = (fieldName) => {
    let isValid = false;
    switch (fieldName) {
      case 'caseName':
        isValid = !!caseName;
        break;
      case 'caseType':
        isValid = !!caseType;
        break;
      case 'specialities':
        isValid = specialities.length > 0 && specialities.some(s => s);
        break;
      case 'assets':
        isValid = assets.length > 0 && assets.some(a => a.name && a.quantity);
        break;
      case 'locationId':
        isValid = !!locationId;
        break;
      case 'areaId':
        isValid = !!areaId;
        break;
      default:
        break;
    }
    setValidatedFields(prev => ({ ...prev, [fieldName]: isValid }));
  };

  const shouldShowValidation = (fieldName) => {
    return touchedFields[fieldName] && !validatedFields[fieldName];
  };

  const handleSubmit = () => {
    const allTouched = Object.keys(touchedFields).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouchedFields(allTouched);

    const allValidated = {
      caseName: !!caseName,
      caseType: !!caseType,
      specialities: specialities.length > 0 && specialities.some(s => s),
      assets: assets.length > 0 && assets.some(a => a.name && a.quantity),
      locationId: !!locationId,
      areaId: !!areaId
    };
    setValidatedFields(allValidated);

    const isFormValid = Object.values(allValidated).every(valid => valid);

    if (isFormValid) {
      onSave({
        id: formData.id,
        caseName,
        caseType,
        specialities,
        assets,
        location_id: locationId,
        area_id: areaId
      });
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">
              {isEditMode ? 'Edit Case Reference' : 'Add Case Reference'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <form>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Case Name</label>
                    <input
                      type="text"
                      className={`form-control ${shouldShowValidation('caseName') ? 'is-invalid' : ''}`}
                      value={caseName}
                      onChange={(e) => setCaseName(e.target.value)}
                      onBlur={() => handleBlur('caseName')}
                      disabled
                    />
                    {shouldShowValidation('caseName') && (
                      <div className="invalid-feedback">Case name is required</div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Case Type</label>
                    <input
                      type="text"
                      className={`form-control ${shouldShowValidation('caseType') ? 'is-invalid' : ''}`}
                      value={caseType}
                      onChange={(e) => setCaseType(e.target.value)}
                      onBlur={() => handleBlur('caseType')}
                      disabled={isEditMode}
                    />
                    {shouldShowValidation('caseType') && (
                      <div className="invalid-feedback">Case type is required</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Specialities Section */}
              <div id="speciality-wrapper">
                <label className="form-label">Specialities</label>
                {shouldShowValidation('specialities') && specialities.every(s => !s) && (
                  <div className="invalid-feedback d-block">At least one speciality is required</div>
                )}
                {specialities.map((spec, idx) => (
                  <div className="row speciality-row mb-2" key={idx}>
                    <div className="col-md-12">
                      <div className="d-flex align-items-center">
                        <SpecialityDropdown
                          value={spec}
                          onChange={(e) => handleSpecialityChange(e.target.value, idx)}
                          className={`flex-grow-1 ${shouldShowValidation('specialities') && !spec ? 'is-invalid' : ''}`}
                          onBlur={() => handleBlur('specialities')}
                        />
                        <button
                          type="button"
                          className="btn btn-link text-success fs-6 mx-2 p-0"
                          onClick={handleAddSpeciality}
                        >
                          <i className="ti ti-circle-plus"></i>
                        </button>
                        {specialities.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link text-danger fs-6 mx-2 p-0"
                            onClick={() => handleRemoveSpeciality(idx)}
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        )}
                      </div>
                      {shouldShowValidation('specialities') && !spec && (
                        <div className="invalid-feedback d-block">Speciality is required</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Assets Section */}
              <div id="asset-wrapper" className="mt-3">
                <label className="form-label">Assets</label>
                {shouldShowValidation('assets') && assets.every(a => !a.name || !a.quantity) && (
                  <div className="invalid-feedback d-block">At least one valid asset is required</div>
                )}
                {assets.map((asset, idx) => (
                  <div className="row asset-row mb-2" key={idx}>
                    <div className="col-md-12">
                      <div className="d-flex align-items-center">
                        <AssetsDropdown
                          value={asset.name}
                          onChange={(e) => handleAssetChange(idx, 'name', e.target.value)}
                          className={`flex-grow-1 ${shouldShowValidation('assets') && !asset.name ? 'is-invalid' : ''}`}
                          onBlur={() => handleBlur('assets')}
                        />
                        <input
                          type="number"
                          className={`form-control qty ms-2 ${shouldShowValidation('assets') && !asset.quantity ? 'is-invalid' : ''}`}
                          style={{ width: '100px' }}
                          placeholder="Qty"
                          value={asset.quantity}
                          onChange={(e) => handleAssetChange(idx, 'quantity', e.target.value)}
                          onBlur={() => handleBlur('assets')}
                          min="1"
                        />
                        <button
                          type="button"
                          className="btn btn-link text-success fs-6 mx-2 p-0"
                          onClick={handleAddAsset}
                        >
                          <i className="ti ti-circle-plus"></i>
                        </button>
                        {assets.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link text-danger fs-6 mx-2 p-0"
                            onClick={() => handleRemoveAsset(idx)}
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Location and Area */}
              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Location</label>
                    <LocationDropdown
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                      onBlur={() => handleBlur('locationId')}
                      hasError={shouldShowValidation('locationId')}
                    />
                    {shouldShowValidation('locationId') && (
                      <div className="invalid-feedback">Location is required</div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Area</label>
                    <AreaDropdown
                      locationId={locationId}
                      value={areaId}
                      onChange={(e) => setAreaId(e.target.value)}
                      onBlur={() => handleBlur('areaId')}
                      hasError={shouldShowValidation('areaId')}
                    />
                    {shouldShowValidation('areaId') && (
                      <div className="invalid-feedback">Area is required</div>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseReferenceAddModal;
